import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Cloud, Shield, Layers } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  const expertise = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'DevOps & Automation',
      description: 'CI/CD pipelines, Infrastructure as Code, and modern software delivery practices.'
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: 'Cloud Architecture & Migration',
      description: 'Cloud strategy, architecture design, and cloud-native engineering principles.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Enterprise IT & Infrastructure',
      description: 'Network infrastructure fundamentals, cybersecurity awareness, and modern IT operations practices.'
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: 'Application & System Modernisation',
      description: 'Web applications, system upgrades, and legacy transformation approaches.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Driving Enterprise Transformation<br />Through DevOps Excellence
          </h1>
          <p className="text-xl text-[#94A3B8] mb-10 max-w-3xl mx-auto leading-relaxed">
            Peabsmart DevOps Training partners with organizations and professionals to build modern DevOps, 
            cloud, and engineering capabilities that accelerate digital transformation and operational efficiency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/courses"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0EA5E9] text-white font-semibold rounded-sm hover:bg-[#0284C7] transition-colors"
            >
              Explore Programs
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-sm border-2 border-white hover:bg-white hover:text-[#1E293B] transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] mb-8">Who We Are</h2>
          <div className="space-y-6 text-[#475569] text-lg leading-relaxed">
            <p>
              Peabsmart DevOps Training is a specialized DevOps and cloud training partner focused on 
              enterprise capability development.
            </p>
            <p>
              We support organizations in strengthening internal engineering teams through structured, 
              hands-on training aligned with modern cloud architecture, DevOps implementation, and 
              infrastructure automation practices.
            </p>
            <p>
              Our programs are designed to align technical skills with real business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] mb-4">Our Expertise</h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Enterprise-aligned capability domains designed to drive operational excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expertise.map((item, idx) => (
              <div 
                key={idx}
                className="bg-white p-8 rounded-sm border border-[#E2E8F0] hover:border-[#0EA5E9] transition-colors"
              >
                <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-sm flex items-center justify-center text-[#0EA5E9] mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#1E293B] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] mb-8">Our Approach</h2>
          <div className="bg-[#F8FAFC] p-8 md:p-12 rounded-sm border border-[#E2E8F0]">
            <p className="text-lg text-[#475569] mb-6 leading-relaxed">
              We combine:
            </p>
            <ul className="space-y-4 text-[#475569] text-lg">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full mt-2.5 flex-shrink-0"></div>
                <span>Practical, real-world labs</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full mt-2.5 flex-shrink-0"></div>
                <span>Enterprise use cases</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full mt-2.5 flex-shrink-0"></div>
                <span>Cloud-based environments</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#0EA5E9] rounded-full mt-2.5 flex-shrink-0"></div>
                <span>Structured implementation frameworks</span>
              </li>
            </ul>
            <p className="text-lg text-[#475569] mt-8 font-medium">
              Our methodology ensures knowledge translates into measurable operational impact.
            </p>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Building the Skills Behind Modern Enterprises
          </h2>
          <p className="text-xl text-[#94A3B8] mb-10">
            Partner with Peabsmart DevOps Training to strengthen your teams and future-proof your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/courses"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0EA5E9] text-white font-semibold rounded-sm hover:bg-[#0284C7] transition-colors"
            >
              View Courses
            </Link>
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-sm border-2 border-white hover:bg-white hover:text-[#1E293B] transition-colors"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
